import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Header from './components/Header';
import SEO from './components/SEO';
import Main from './pages/Main';
import Category from './pages/Category';
import Collection from './pages/Collection';
import Details from './pages/Details';
import SearchPage from './pages/SearchPage';
import Modal from './components/Modal';
import ScreenSizeInfoProvider from './ScreenSizeInfoProvider';
import ErrorBoundary from '../../lib/ErrorBoundary';
import { CATEGORY } from '../../lib/constants';
import QuickLinks from './components/QuickLinks';

const buildDetailsSlug = detailsSlug => (detailsSlug ? 'details' : 'main');
const buildCategorySlug = categorySlug =>
  categorySlug === CATEGORY.COLLECTION ? categorySlug : 'category';
const buildDisplayName = (categorySlug, detailsSlug) =>
  categorySlug
    ? buildCategorySlug(categorySlug)
    : buildDetailsSlug(detailsSlug);

const AppCenter = props => {
  const {
    fptiImpression,
    location: { pathname },
    structuredPSData,
    productIntentTools,
    fptiClick,
    flatPSData = {},
    dispatch,
    toggleToolFavorite,
    tools,
    clearSearch,
    app,
    toolServFavorites,
    appSearch,
    staticContext,
    experimentation,
    quicklinks
  } = props;
  const { content = {}, categories = [] } = structuredPSData;
  const { defaultRecommended = [] } = flatPSData;
  const {
    coloredIconsExp: isColoredIconsEligible,
    v2LayoutExp: isNewLayoutEligible,
    twofersExp: isTwofersEligible,
    searchModalExp: isSearchModalEligible
  } = experimentation;

  const shouldShowModal = isSearchModalEligible && isNewLayoutEligible;
  // the CategoryToolTile is nested several layers deep in a few places
  // group all these props together to make it easier to pass them down
  // as they're only used by CategoryToolTile component
  const toolTileProps = {
    flatPSData,
    dispatch,
    toggleToolFavorite,
    tools,
    app,
    toolServFavorites,
    productIntentTools,
    experimentation
  };

  const onKeyDownCloseModal = e => {
    if (e.key === 'Escape' && shouldShowModal && appSearch.active) {
      dispatch(clearSearch());
    }
  };
  const onClickCloseModal = () => {
    if (shouldShowModal && appSearch.active) {
      dispatch(clearSearch());
    }
  };
  // When route changes, scroll to top and take impression of page.
  useEffect(() => {
    window.scrollTo(0, 0);
    fptiImpression({ amplitude: true });
  }, [pathname]);

  const [, , categorySlug, detailsSlug] = pathname.split('/');
  const isCollectionsPage = categorySlug === 'collection';

  const getPage = (categorySlug, detailsSlug) => {
    if (props.appSearch.query && !isSearchModalEligible) {
      return (
        <SearchPage
          toolTileProps={toolTileProps}
          fptiClick={fptiClick}
          appSearch={appSearch}
          deviceInfo={app?.deviceInfo}
          fptiImpression={props.fptiImpression}
          dispatch={dispatch}
          clearSearch={clearSearch}
          quicklinks={quicklinks}
          experimentation={experimentation}
        />
      );
    }

    if (isCollectionsPage && detailsSlug) {
      const { collections = [] } = flatPSData;
      const collection = collections.find((col = {}) => {
        return col.location === detailsSlug;
      });
      return (
        <Collection
          {...props}
          toolTileProps={toolTileProps}
          collection={collection}
        />
      );
    } else if (detailsSlug) {
      return (
        <Details
          {...props}
          toolTileProps={toolTileProps}
          defaultRecommended={flatPSData.defaultRecommended}
          isTwofersEligible={isTwofersEligible}
        />
      );
    } else if (categorySlug) {
      const category = categories.find(
        ({ content = {} }) => content.location === categorySlug
      );
      const { staticContext = {}, app } = props;
      return (
        <Category
          category={category}
          fptiClick={fptiClick}
          fptiImpression={fptiImpression}
          toolTileProps={toolTileProps}
          staticContext={staticContext}
          app={app}
          isColoredIconsEligible={isColoredIconsEligible}
          isNewLayoutEligible={isNewLayoutEligible}
        />
      );
    }
    return (
      <Main
        structuredPSData={structuredPSData}
        fptiClick={fptiClick}
        fptiImpression={fptiImpression}
        toolTileProps={toolTileProps}
        defaultRecommended={flatPSData.defaultRecommended}
        experimentation={experimentation}
        quicklinks={quicklinks}
        appSearch={appSearch}
        clearSearch={clearSearch}
        dispatch={dispatch}
      />
    );
  };
  const {
    structuredPSData: { content: { imageUrl = '' } = {} } = {},
    location = {},
    seo = {},
    app: {
      deviceInfo: { isDesktop } = {},
      context: {
        user: { isLoggedIn } = {},
        location: { cdn: cdnHost = 'www.paypalobjects.com' } = {}
      } = {}
    } = {},
    experimentation: { moduleFederation }
  } = props;
  const displayName = buildDisplayName(categorySlug, detailsSlug);

  // TODO - Fix SEO for Collections
  return (
    <ErrorBoundary fptiImpression={fptiImpression}>
      <ScreenSizeInfoProvider staticContext={staticContext}>
        <div
          id="merchanthub-appcenter"
          aria-hidden="true"
          className={displayName}
          onKeyDown={onKeyDownCloseModal}
          onClick={onClickCloseModal}
        >
          <SEO
            mainImageUrl={imageUrl}
            seoData={seo}
            location={location}
            categorySlug={categorySlug}
            detailsSlug={detailsSlug}
            app={app}
          />
          <Header {...props} />
          {isSearchModalEligible && (
            <div id="appcenter-main">
              <Modal
                toolTileProps={toolTileProps}
                fptiClick={fptiClick}
                appSearch={appSearch}
                featured={defaultRecommended}
                deviceInfo={toolTileProps.app?.deviceInfo}
                fptiImpression={fptiImpression}
                dispatch={dispatch}
                clearSearch={clearSearch}
                quicklinks={quicklinks}
                content={content}
                shouldShowModal={shouldShowModal}
                experimentation={experimentation}
              />
            </div>
          )}

          {/*render in root view on mobile since the footer is sticky to all pages */
          !isDesktop && !isNewLayoutEligible && (
            <QuickLinks
              moduleFederation={moduleFederation}
              isLoggedIn={isLoggedIn}
              isDesktop={isDesktop}
              quicklinks={quicklinks}
              cdnHost={cdnHost}
            />
          )}
          {getPage(categorySlug, detailsSlug)}
        </div>
      </ScreenSizeInfoProvider>
    </ErrorBoundary>
  );
};

AppCenter.propTypes = {
  toolServFavorites: PropTypes.array,
  app: PropTypes.object,
  dispatch: PropTypes.func,
  location: PropTypes.object,
  fptiClick: PropTypes.func,
  fptiImpression: PropTypes.func,
  toggleToolFavorite: PropTypes.func,
  tools: PropTypes.object,
  structuredPSData: PropTypes.object,
  flatPSData: PropTypes.object,
  productIntentTools: PropTypes.object,
  seo: PropTypes.object,
  staticContext: PropTypes.object,
  appSearch: PropTypes.shape({
    query: PropTypes.string,
    active: PropTypes.bool,
    results: PropTypes.arrayOf(PropTypes.string)
  }),
  clearSearch: PropTypes.func.isRequired,
  recommendations: PropTypes.array,
  experimentation: PropTypes.object,
  quicklinks: PropTypes.object,
  isColoredIconsEligible: PropTypes.bool
};

export default withRouter(AppCenter);
